import type { ModerationEntityType, Translate } from '@/entities/moderation';
import type { ModerationReview } from '../../model/use-moderation-review';
import { InlineDecisionFooter } from './inline-decision-footer';
import { PendingModifierGroupsSection } from './pending-modifier-groups-section';
import { RequestInfo, RequestMeta } from './request-meta';
import { ReviewAlerts } from './review-alerts';
import { decisionLabels, reviewTitle } from './review-copy';
import { ReviewModalShell } from './review-modal-shell';
import { EntitySnapshot } from './snapshots/entity-snapshot';
import { VerdictBanner } from './verdict-banner';

interface SnapshotReviewModalProps {
  entityType: ModerationEntityType;
  review: ModerationReview;
  t: Translate;
  onClose: () => void;
}

/**
 * Single-column review — every state except a pending edit:
 * - create (pending / decided): the proposed entity as a whole;
 * - delete (pending / decided): the entity being removed;
 * - update (decided): the request's own field-by-field record, then the
 *   entity as it is live now.
 * Pending requests get the inline decision footer; settled ones the verdict
 * banner and no actions.
 */
export function SnapshotReviewModal({
  entityType,
  review,
  t,
  onClose,
}: SnapshotReviewModalProps) {
  const { request, operation, decision } = review;
  const labels = decisionLabels(operation, t);
  const loading = review.cardStatus === 'loading';
  const hasVerdictInfo = decision === 'approved' || decision === 'rejected';
  const verdictInfo = (
    <>
      <RequestInfo request={request} t={t} />
      {hasVerdictInfo && (
        <VerdictBanner
          request={request}
          decision={decision}
          operation={operation}
          t={t}
          inline
        />
      )}
    </>
  );

  return (
    <ReviewModalShell
      title={reviewTitle(entityType, operation, t)}
      meta={<RequestMeta request={request} operation={operation} t={t} />}
      banners={
        <>
          <ReviewAlerts review={review} t={t} />
        </>
      }
      footer={
        decision === 'pending' ? (
          <InlineDecisionFooter
            entityType={entityType}
            changeRequestId={request.id}
            approveLabel={labels.approve}
            rejectLabel={labels.reject}
            onDone={onClose}
          />
        ) : undefined
      }
      onClose={onClose}
    >
      <div
        className="flex flex-col gap-[var(--form-gap-y)]"
        data-testid="moderation-snapshot"
      >
        <EntitySnapshot
          entityType={entityType}
          entity={review.snapshot}
          weekSchedule={
            operation === 'create'
              ? (review.ownSlot?.version.week_schedule ??
                review.snapshot.week_schedule ??
                review.card?.prod?.week_schedule)
              : (review.card?.prod?.week_schedule ?? review.snapshot.week_schedule)
          }
          request={request}
          loading={loading}
          t={t}
          verdictInfo={verdictInfo}
        />
      </div>
      {entityType === 'products' && review.card && (
        <PendingModifierGroupsSection
          entityType={entityType}
          groups={review.card.modifier_groups}
          t={t}
        />
      )}
    </ReviewModalShell>
  );
}
